import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { getFeaturedServices } from '../../services/v2ServicesRepository';

const COPY = {
  en: {
    eyebrow: 'FEATURED SERVICES',
    heading: 'Where illumi.dev usually starts.',
    lede: 'Three focused engagements designed to bring clarity, reduce friction, and create a stronger foundation for growth.',
    explore: 'Explore service →',
    exploreSrPrefix: 'Explore',
    viewAll: 'View all services →',
    loading: 'Loading services…',
    emptyHeading: 'New services are being prepared.',
    emptyBody: 'The service catalog is being updated. You can still request a Business Systems Audit in the meantime.',
    errorHeading: 'Services are temporarily unavailable.',
    errorBody: 'You can still request a Business Systems Audit or contact illumi.dev directly.',
  },
  // Landing stays English-only in Phase 8; keys stubbed for future use only.
};

const _devWarn = (msg, err) => {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(msg, err);
  }
};

const FeaturedCard = ({ service, t, language = 'en' }) => {
  if (!service) return null;
  return (
    <Link
      to={`/v2/services/${service.slug}?lang=${language}`}
      className="iv2-featured-card"
      data-testid={`iv2-featured-service-card-${service.slug}`}
    >
      {service.kicker && (
        <span className="iv2-featured-card-kicker">{service.kicker}</span>
      )}
      <h3 className="iv2-featured-card-title">{service.title}</h3>
      <p className="iv2-featured-card-desc">{service.short_description}</p>
      <span className="iv2-featured-card-more" aria-hidden="true">
        {t.explore}
      </span>
      <span className="iv2-sr-only">{`${t.exploreSrPrefix} ${service.title}`}</span>
    </Link>
  );
};

const SkeletonCard = () => (
  <div className="iv2-featured-card iv2-featured-skeleton" aria-hidden="true">
    <span className="iv2-featured-skeleton-kicker" />
    <span className="iv2-featured-skeleton-title" />
    <span className="iv2-featured-skeleton-line" />
    <span className="iv2-featured-skeleton-line short" />
    <span className="iv2-featured-skeleton-more" />
  </div>
);

const FeaturedServicesV2 = ({ language = 'en' }) => {
  const t = COPY[language] || COPY.en;
  const [state, setState] = useState({ status: 'loading', data: [], error: null });
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    setState({ status: 'loading', data: [], error: null });
    // De-dupe consumer against unmount / language changes.
    let cancelled = false;
    getFeaturedServices({ lang: language, limit: 3 })
      .then((data) => {
        if (cancelled || !aliveRef.current) return;
        setState({ status: 'success', data: data || [], error: null });
      })
      .catch((err) => {
        if (cancelled || !aliveRef.current) return;
        _devWarn('Featured services repository failed', err);
        setState({ status: 'error', data: [], error: err });
      });
    return () => {
      cancelled = true;
      aliveRef.current = false;
    };
  }, [language]);

  const isEmpty = state.status === 'success' && state.data.length === 0;
  const isSuccess = state.status === 'success' && state.data.length > 0;

  return (
    <section
      id="featured-services"
      className="iv2-featured-services"
      data-testid="iv2-featured-services"
      aria-labelledby="iv2-featured-services-heading"
    >
      <div className="iv2-container">
        <p className="iv2-section-eyebrow">{t.eyebrow}</p>
        <h2
          id="iv2-featured-services-heading"
          className="iv2-featured-services-heading"
          data-testid="iv2-featured-services-heading"
        >
          {t.heading}
        </h2>
        <p className="iv2-featured-services-lede">{t.lede}</p>

        {state.status === 'loading' && (
          <>
            <p className="iv2-sr-only" role="status" aria-live="polite" data-testid="iv2-featured-services-loading">
              {t.loading}
            </p>
            <div className="iv2-featured-services-grid" data-testid="iv2-featured-services-grid" aria-hidden="true">
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </div>
          </>
        )}

        {isSuccess && (
          <>
            <div className="iv2-featured-services-grid" data-testid="iv2-featured-services-grid">
              {state.data.slice(0, 3).map((svc) => (
                <FeaturedCard key={svc.id} service={svc} t={t} language={language} />
              ))}
            </div>
            <div className="iv2-featured-services-viewall">
              <Link
                to={`/v2/services?lang=${language}`}
                className="iv2-featured-services-viewall-link"
                data-testid="iv2-featured-services-view-all"
              >
                {t.viewAll}
              </Link>
            </div>
          </>
        )}

        {isEmpty && (
          <div className="iv2-featured-services-notice" data-testid="iv2-featured-services-empty">
            <h3 className="iv2-featured-services-notice-heading">{t.emptyHeading}</h3>
            <p className="iv2-featured-services-notice-body">{t.emptyBody}</p>
          </div>
        )}

        {state.status === 'error' && (
          <div className="iv2-featured-services-notice" role="alert" data-testid="iv2-featured-services-error">
            <h3 className="iv2-featured-services-notice-heading">{t.errorHeading}</h3>
            <p className="iv2-featured-services-notice-body">{t.errorBody}</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default FeaturedServicesV2;
